import { useEffect, useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { cachedGet, postJson } from '../../lib/api.js'
import { labPatientName } from '../../lib/lab.js'
import { Modal, DrawerFooter, SearchInput, Loading, EmptyState, InlineError } from '../../kit'

// "ใครเป็นคนทำงานชิ้นนี้" — asked at the moment a technician marks a piece
// LAB ทำงานเสร็จ, which is when the answer is actually known. The list is the
// employees holding the Lab role (GET /api/lab/makers, projected to 4 safe fields).
//
// Defaults to the logged-in technician, since the common case by far is that the
// person marking it done is the person who made it — but they can name a colleague.
// Confirming writes lab.retainer.maker and only then does the stage move proceed;
// cancelling leaves the stage untouched.
//
// Kit Modal: Esc / backdrop / ✕ cancel (each resolves the stage move as abandoned);
// none of them close while the write is running.
export default function MakerPicker({ row, me, onSaved, onCancel }) {
  const [makers, setMakers] = useState(null)
  const [err, setErr] = useState('')
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(Number(row?.retainer?.maker) || 0)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    cachedGet('/api/lab/makers')
      .then((list) => {
        if (!alive) return
        // The endpoint returns the whole active roster (the drawer needs it for code
        // lookups); only Lab-role staff can be named as the maker.
        const arr = (Array.isArray(list) ? list : []).filter((m) => m.lab)
        setMakers(arr)
        // Pre-select the current user when they are themselves a Lab employee.
        setSel((cur) => cur || (arr.some((m) => m.id === Number(me?.employee_id)) ? Number(me.employee_id) : 0))
      })
      .catch((e) => alive && setErr(e?.message || 'โหลดรายชื่อช่างไม่สำเร็จ'))
    return () => { alive = false }
  }, [me?.employee_id])

  const shown = useMemo(() => {
    const list = makers || []
    const s = q.trim().toLowerCase()
    if (!s) return list
    return list.filter((m) => [m.code, m.name, m.nickname].some((v) => String(v || '').toLowerCase().includes(s)))
  }, [makers, q])

  async function save() {
    if (!sel || saving) return
    setSaving(true); setErr('')
    try {
      await postJson('/api/lab/maker', {
        lab_id: row.id,
        maker: sel,
        // POST /lab replaces the whole retainer object — send the current one back
        // so nothing but `maker` changes. `detail` has no upstream fallback either.
        retainer: row.retainer || {},
        detail: row.detail || '',
      })
      onSaved(sel)
    } catch (e) {
      setErr(e?.message || 'บันทึกไม่สำเร็จ')
      setSaving(false)
    }
  }

  return (
    <Modal
      onClose={onCancel}
      busy={saving}
      width="sm"
      title="ใครเป็นคนทำงานชิ้นนี้"
      subtitle={<span className="block truncate">{labPatientName(row)} · Lab #{row.id}</span>}
      footer={
        <DrawerFooter
          onCancel={onCancel}
          onSave={save}
          saving={saving}
          canSave={!!sel}
          saveLabel="บันทึกและเปลี่ยนสถานะ"
          status={!sel && makers?.length ? 'เลือกช่างก่อนบันทึก' : undefined}
        />
      }
    >
      <SearchInput value={q} onChange={setQ} placeholder="ค้นหาชื่อ/รหัสช่าง…" className="mb-3" />

      <div className="space-y-1">
        {makers == null && !err && <Loading />}
        {makers && shown.length === 0 && (
          <EmptyState variant="plain" title={makers.length === 0 ? 'ยังไม่มีพนักงานที่มี role "Lab"' : 'ไม่พบช่างที่ค้นหา'} />
        )}
        {shown.map((m) => (
          <button key={m.id} type="button" onClick={() => setSel(m.id)} aria-pressed={sel === m.id}
            className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition ${sel === m.id ? 'border-brand-400 bg-brand-50' : 'border-slate-200 hover:border-slate-300'}`}>
            <span className="text-[13.5px] font-medium text-slate-800 truncate">{m.code || m.name || `#${m.id}`}</span>
            {m.nickname && <span className="text-[12px] text-slate-400 truncate">{m.nickname}</span>}
            {sel === m.id && <Check size={15} className="ml-auto shrink-0 text-brand-600" strokeWidth={3} />}
          </button>
        ))}
      </div>

      {err && <div className="mt-3"><InlineError>{err}</InlineError></div>}
    </Modal>
  )
}
